<template>
  <div
    v-if="visible"
    class="tier-auto-upgrade"
  >
    <!-- Compact toggle row : label + switch. Lives right under the tier
         dropdown inside the PlanComparator topbar — no card chrome, just
         a discreet inline control. -->
    <label class="tier-auto-upgrade__toggle">
      <span class="tier-auto-upgrade__toggle-label">
        {{ t('billing.tierAutoUpgrade.title') }}
      </span>
      <InputSwitch
        :model-value="enabled"
        class="tier-auto-upgrade__switch"
        @update:model-value="onToggleEnabled"
      />
    </label>

    <transition name="tier-auto-upgrade-expand">
      <div
        v-if="enabled"
        class="tier-auto-upgrade__hint-row"
      >
        <template v-if="hasOptions">
          <!-- Display mode : hint sentence with an inline pen icon. Clicking
               the icon swaps the sentence for the dropdown (edit mode). -->
          <template v-if="!editing">
            <p class="tier-auto-upgrade__hint">
              {{ t('billing.tierAutoUpgrade.hint', {
                current: formatNumber(currentTier),
                max: formatNumber(maxTier),
              }) }}
            </p>
            <button
              type="button"
              class="tier-auto-upgrade__edit"
              :aria-label="t('billing.tierAutoUpgrade.editAria')"
              :title="t('billing.tierAutoUpgrade.editAria')"
              @click="startEditing"
            >
              <i class="fa-regular fa-pen" />
            </button>
          </template>
          <!-- Edit mode : inline dropdown, auto-opened. Closes on choice
               or on outside click (@hide). -->
          <Dropdown
            v-else
            ref="dropdownRef"
            :model-value="maxTier"
            :options="maxTierOptions"
            option-label="label"
            option-value="value"
            class="tier-auto-upgrade__dropdown"
            append-to="body"
            @update:model-value="onMaxTierChange"
            @hide="editing = false"
          />
        </template>
        <p
          v-else
          class="tier-auto-upgrade__hint tier-auto-upgrade__hint--maxed"
        >
          {{ t('billing.tierAutoUpgrade.maxedOut') }}
        </p>
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
import {
  computed, defineComponent, nextTick, PropType, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import InputSwitch from 'primevue/inputswitch';
import Dropdown from 'primevue/dropdown';

export default defineComponent({
  name: 'TierAutoUpgradeBlock',

  components: { InputSwitch, Dropdown },

  props: {
    /**
     * The tier the user has chosen for their subscription. Used as the
     * floor for the plafond dropdown : the customer can only cap at a
     * strictly higher tier (`currentTier + 1` → grid max).
     */
    currentTier: {
      type: Number as PropType<number>,
      required: true,
    },
    /**
     * The full pricing grid. Tiers strictly above `currentTier` populate
     * the plafond dropdown. When empty (shop already on the highest tier),
     * the block degrades to a "maxedOut" message.
     */
    tiers: {
      type: Array as PropType<Array<{ threshold: number }>>,
      default: () => [],
    },
    /** v-model: `tierAutoUpgradeEnabled`. Defaults to true. */
    enabled: {
      type: Boolean,
      default: true,
    },
    /**
     * v-model: `tierAutoUpgradeMax`. Initialised by the parent
     * (PlanComparator) — the block is a pure presenter and never
     * self-initialises this value.
     */
    maxTier: {
      type: Number as PropType<number>,
      default: 0,
    },
  },

  emits: ['update:enabled', 'update:maxTier'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const editing = ref(false);
    const dropdownRef = ref<any>(null);

    const formatNumber = (n: number) => new Intl.NumberFormat('fr-FR').format(n);

    // Visible as soon as a tier is selected — the toggle is always usable.
    const visible = computed(() => props.currentTier > 0);

    // Eligible plafond tiers = strictly above the current tier. Sorted
    // ascending for predictable dropdown order.
    const maxTierOptions = computed(() => {
      const sorted = [...props.tiers]
        .map((tier) => tier.threshold)
        .filter((th) => th > props.currentTier)
        .sort((a, b) => a - b);
      return sorted.map((th) => ({
        value: th,
        label: t('billing.tierAutoUpgrade.option', { count: formatNumber(th) }),
      }));
    });

    const hasOptions = computed(() => maxTierOptions.value.length > 0);

    const onToggleEnabled = (v: boolean) => {
      emit('update:enabled', v);
    };
    const onMaxTierChange = (v: number) => {
      emit('update:maxTier', v);
      editing.value = false;
    };

    // Swap the hint paragraph for the dropdown and open it on the next
    // tick — saves a second click for the user.
    const startEditing = async () => {
      editing.value = true;
      await nextTick();
      dropdownRef.value?.show?.();
    };

    return {
      t,
      visible,
      editing,
      dropdownRef,
      maxTierOptions,
      hasOptions,
      formatNumber,
      onToggleEnabled,
      onMaxTierChange,
      startEditing,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/utilities/variables";

// Inline auto-tier-upgrade : a switch row + a hint sentence with an
// inline edit icon. No card, no panel — sits naturally under the tier
// dropdown inside the PlanComparator topbar.
.tier-auto-upgrade {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;

  &__toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    cursor: pointer;
    padding: 2px 0;
  }

  &__toggle-label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #1f2937;
  }

  &__switch {
    flex-shrink: 0;

    // Compact switch matching the inline footprint.
    :deep(.p-inputswitch) {
      width: 2rem;
      height: 1.125rem;
    }
    :deep(.p-inputswitch-slider:before) {
      width: 0.875rem;
      height: 0.875rem;
      left: 0.125rem;
      margin-top: -0.4375rem;
    }
    :deep(.p-inputswitch.p-inputswitch-checked .p-inputswitch-slider:before) {
      transform: translateX(0.875rem);
    }
  }

  // Hint row : sentence + inline pen icon on the right, or the dropdown
  // in edit mode. Aligned baseline so the icon sits with the text.
  &__hint-row {
    display: flex;
    align-items: center;
    gap: 6px;
    padding-top: 4px;
  }

  &__hint {
    font-size: 0.6875rem;
    color: #4b5563;
    margin: 0;
    line-height: 1.4;
    flex: 1;
    // Force left alignment — without this, the wrapped second line
    // looks centered under the first which is visually off vs the
    // rest of the comparator's left-aligned copy.
    text-align: left;

    &--maxed {
      font-style: italic;
    }
  }

  &__edit {
    background: transparent;
    border: 0;
    padding: 4px 6px;
    border-radius: 6px;
    color: #629d03;
    cursor: pointer;
    font-size: 0.75rem;
    line-height: 1;
    transition: background 120ms ease;
    flex-shrink: 0;

    &:hover {
      background: rgba(148, 200, 64, 0.16);
    }

    &:focus-visible {
      outline: 2px solid rgba(148, 200, 64, 0.5);
      outline-offset: 1px;
    }
  }

  &__dropdown {
    width: 100%;

    :deep(.p-dropdown) {
      width: 100%;
      border: 1px solid #c8dc9a;
      background: #fff;
      transition: box-shadow 160ms ease;
    }

    :deep(.p-dropdown:hover),
    :deep(.p-dropdown:focus-within) {
      box-shadow: 0 0 0 3px rgba(148, 200, 64, 0.18);
    }

    :deep(.p-dropdown-label) {
      padding: 6px 10px;
      font-size: 0.75rem;
      font-weight: 600;
      color: #0f172a;
    }

    :deep(.p-dropdown-trigger) {
      color: $brand-color-primary;
      width: 1.75rem;
    }
  }
}

.tier-auto-upgrade-expand-enter-active,
.tier-auto-upgrade-expand-leave-active {
  transition: opacity 180ms ease, max-height 180ms ease;
  overflow: hidden;
}
.tier-auto-upgrade-expand-enter-from,
.tier-auto-upgrade-expand-leave-to {
  opacity: 0;
  max-height: 0;
}
.tier-auto-upgrade-expand-enter-to,
.tier-auto-upgrade-expand-leave-from {
  opacity: 1;
  max-height: 120px;
}
</style>
